<template>
  <AppDialog
    class="insights-unusual-activity-member-dialog"
    append-to-body
    :show="show"
    size="large"
    :title="title"
    @close="closeDialog"
  >
    <template #titleLabel>
      <button
        :key="linkCopied"
        ref="copyLinkBtn"
        v-tooltip
        :title="linkCopied ? 'Copied to your clipboard' : 'Copy link'"
        class="copy-link-btn"
        type="button"
        @click="copyLink"
      >
        <i :class="['hi', linkCopied ? 'hi-check' : 'hi-link', 'hi-12']" />
      </button>
    </template>
    <div class="d-flex justify-space-between align-items-center -mt-20 mb-20 flex-wrap gap-20">
      <div class="d-flex align-items-center gap-5">
        <div
          v-if="currentDashboardUser"
          class="d-flex align-items-center"
        >
          <AppAvatar
            size="speck"
            :avatar="currentDashboardUser.avatar"
          />
          <span>{{ currentDashboardUser.name }}</span>
        </div>
        <template v-if="unusualActivitiesCount">
          <span>|</span>
          <span>{{ unusualActivitiesCount }} {{ $filters.pluralize(unusualActivitiesCount, 'instance') }}</span>
        </template>
      </div>
      <div>
        <InsightsViewFullDayLink
          v-if="unusualActivities[0] && currentDashboardUser"
          :date="unusualActivities[0].starts_at"
          :organization-id="organizationId"
          :user-id="currentDashboardUser.id"
          :time-zone="timeZone"
          event="Unusual link to screenshot page"
        >
          <span>Investigate all activity</span>
        </InsightsViewFullDayLink>
      </div>
    </div>

    <div
      v-if="selectedInstance"
      class="d-flex relative gap-10"
    >
      <div class="scroll-wrapper">
        <div class="d-flex flex-column scroll-container pr-20">
          <div class="d-flex flex-column gap-10">
            <div
              v-for="(ua, index) in unusualActivities"
              :key="`${ua.type}-${index}`"
              class="card d-flex flex-column cursor-pointer gap-10 p-10"
              :class="{ selected: index === selected }"
              @click="select(index)"
            >
              <div>
                <InsightsUnusualActivityConfidenceBadge :confidence-level="ua.confidence_level" />
              </div>
              <div
                v-if="ua"
                class="text-13"
              >
                <strong>{{ activityLabel(ua.type).text }}</strong>
                <div>{{ formatTimeRange(ua.starts_at, ua.stops_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-auto">
        <h4 class="m-0">{{ activityLabel(selectedInstance.type).text }}</h4>
        <div class="text-13 z-text-gray-600">
          {{ selectedInstance.description }}.
          <a
            v-track:click="{ label: 'Unusual learn more', location: 'Individual instance modal' }"
            target="_blank"
            :href="activityLabel(selectedInstance.type).url"
          >
            Learn more
          </a>
        </div>

        <div class="mt-30 mb-20 grid gap-40">
          <div class="flex-auto">
            <div class="control-label d-block mb-4">DATE & TIME</div>
            <div class="text-13">{{ formatDateWithZone(selectedInstance.starts_at, selectedInstance.stops_at) }}</div>
            <div class="text-13">{{ formatRange(selectedInstance.starts_at, selectedInstance.stops_at) }}</div>
          </div>
          <div class="flex-auto">
            <div class="control-label d-block mb-4">DURATION</div>
            <div>{{ formatDuration(selectedInstance.starts_at, selectedInstance.stops_at) }}</div>
          </div>
          <div class="flex-auto">
            <div class="control-label d-block no-wrap mb-10">OVERALL ACTIVITY</div>

            <div v-if="isNull(selectedInstance.percentage)">—</div>
            <div
              v-else
              class="badge border-rounded"
            >
              {{ Math.round(selectedInstance.percentage) }}%
            </div>
          </div>

          <div class="flex-auto">
            <div class="control-label d-block mb-10">ACTIVITY BREAKDOWN</div>
            <div class="activity-breakdown small w-full">
              <div class="mouse">
                <div class="progress-bar z-bg-green-100 w-full">
                  <div
                    class="progress-bar green"
                    :style="{ width: `${selectedInstanceBreakdown.mouse}%` }"
                  />
                </div>
                <div class="percentage">
                  <div class="legend green"></div>
                  <div class="no-wrap">{{ secondsToMinutes(selectedInstance.mouse) }}m - Mouse</div>
                </div>
              </div>

              <div class="keyboard">
                <div class="progress-bar z-bg-orange-100 w-full">
                  <div
                    class="progress-bar orange"
                    :style="{ width: `${selectedInstanceBreakdown.keyboard}%` }"
                  />
                </div>
                <div class="percentage">
                  <div class="legend orange"></div>
                  <div class="no-wrap">{{ secondsToMinutes(selectedInstance.keyboard) }}m - Keyboard</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="my-20">
          <div class="control-label d-block mb-10">SCREENSHOTS FOR THIS INSTANCE</div>
          <div
            v-if="hasScreenshots"
            class="screen-count-pill"
            :class="{ 'cursor-pointer': galleryEnabled, 'cursor-disabled': !galleryEnabled }"
            @click="showGallery"
          >
            {{ screens.length }} {{ $filters.pluralize(screens.length, 'screen', 'screens') }}
            <AppGallery
              v-if="galleryEnabled"
              ref="gallery"
              :gallery-options="{ dynamic: true }"
            >
              <AppGalleryItem
                v-for="(screenshot, index) in screens"
                :key="`${screenshot.index}_${index}`"
                class="hidden"
                :url="screenshot.original_url"
                :thumb-url="screenshot.thumb_url"
                :caption="screenshotCaption(screenshot)"
                :fallback-image-url="ACTIVITIES_SCREENSHOT_PENDING_ORIGINAL"
              />
            </AppGallery>
          </div>
          <div v-else>—</div>
        </div>

        <div class="my-20">
          <div class="control-label d-block mb-10">TOP APPS IN USE</div>
          <template v-if="topAppsByPercentage.length">
            <div class="d-flex flex-wrap gap-8">
              <div
                v-for="(app, index) in topAppsByPercentage"
                :key="`${app[0]}-${index}`"
                v-tooltip
                class="d-inline-flex z-bg-gray-100 small rounded-4 strong px-6 py-2"
                :style="suspiciousAppStyle(app[0])"
                :title="suspiciousAppStyle(app[0]) ? 'Known suspicious app' : ''"
              >
                {{ app[0] }}
                <template v-if="app[1]"> - {{ app[1] }}%</template>
              </div>
            </div>
          </template>
          <div v-else>—</div>
        </div>

        <div class="my-20">
          <div class="control-label d-block mb-10">TOP URLS VISITED</div>
          <template v-if="topUrlsByPercentage.length">
            <div class="d-flex flex-wrap gap-8">
              <div
                v-for="app in topUrlsByPercentage"
                :key="app"
                class="d-inline-flex z-bg-gray-100 small rounded-4 strong px-6 py-2"
              >
                {{ app[0] }} - {{ app[1] }}%
              </div>
            </div>
          </template>
          <div v-else>—</div>
        </div>
      </div>
    </div>
  </AppDialog>
</template>

<script>
import moment from 'moment-timezone';
import { mapActions, mapGetters, mapState } from 'vuex';
import { Insights, InsightsTracking } from '~/mixins';
import { INSIGHTS } from '~/app_constants';

import { isNull, kebabCase, padStart, toPairs, sortBy, clamp } from 'lodash-es';
import InsightsViewFullDayLink from '~/components/insights/InsightsViewFullDayLink.vue';
import InsightsUnusualActivityConfidenceBadge from '~/components/insights/InsightsUnusualActivityConfidenceBadge.vue';
import { parseInternalDatetime, secondsToHours, formatDate } from '~/utils/time_utils';
import { ACTIVITIES_SCREENSHOT_PENDING_ORIGINAL } from '~/rails_assets';

export default {
  name: 'InsightsUnusualActivityMemberDialog',
  components: {
    InsightsUnusualActivityConfidenceBadge,
    InsightsViewFullDayLink,
  },
  mixins: [Insights, InsightsTracking],
  props: {
    dialogDate: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: false,
    },
    timeZone: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'select'],
  data: () => ({
    ACTIVITIES_SCREENSHOT_PENDING_ORIGINAL,
    filterId: 'all',
    firstLoad: true,
    isFullyLoaded: false,
    linkCopied: false,
    selected: 0,
    tooltipContent: INSIGHTS.tooltips.unusualActivity.content,
    widgetData: {},
    widgetKey: INSIGHTS.api.unusualActivity.user,
  }),
  computed: {
    ...mapState(['widgets']),
    ...mapGetters(['currentDashboardUser', 'organizationId']),
    dataPrivacyOn() {
      return this.widgetData?.tools_hidden;
    },
    galleryEnabled() {
      // Check if screens[0] exists and has a URL
      return this.screens[0]?.original_url;
    },
    hasScreenshots() {
      return !!this.screens?.length;
    },
    isLoading() {
      return !!this.loadingWidgets?.[this.widgetKey];
    },
    proxyUser() {
      return this.widgetData?.proxy_user;
    },
    screens() {
      return this.selectedInstance.screens || [];
    },
    selectedInstance() {
      return this.unusualActivities[this.selected];
    },
    selectedInstanceBreakdown() {
      const overall = Number(this.selectedInstance.overall) || 0;
      const mouse = Number(this.selectedInstance.mouse) || 0;
      const keyboard = Number(this.selectedInstance.keyboard) || 0;

      if (overall > 0) {
        return {
          mouse: clamp(Math.round((mouse / overall) * 100), 0, 100),
          keyboard: clamp(Math.round((keyboard / overall) * 100), 0, 100),
        };
      }

      return {
        mouse: 0,
        keyboard: 0,
      };
    },
    title() {
      return formatDate(moment(this.dialogDate));
    },
    topApps() {
      return this.selectedInstance.top_apps;
    },
    topAppsByPercentage() {
      const orderedApps = this.orderByPercentage(this.topApps);
      const suspicious = orderedApps.filter(([app]) => this.selectedInstance.suspicious_apps?.includes(app));
      const normal = orderedApps.filter(([app]) => !this.selectedInstance.suspicious_apps?.includes(app));

      return [...suspicious, ...normal].slice(0, 5);
    },
    topUrls() {
      return this.selectedInstance.top_urls;
    },
    topUrlsByPercentage() {
      return this.orderByPercentage(this.topUrls).slice(0, 5);
    },
    unusualActivities() {
      const data = this.widgetData?.list || [];

      // We are reversing the array so the newest ones are on top of the list
      return [...data].reverse();
    },
    unusualActivitiesCount() {
      return this.widgetData?.total || 0;
    },
  },
  async created() {
    await this.fetchWidgets({
      widgetKey: this.widgetKey,
      extraQueryParams: {
        time_zone: this.timeZone,
        from: this.dialogDate,
        to: this.dialogDate,
      },
    });
    this.widgetData = this.widgets[this.widgetKey];
    const index = parseInt(this.filters.index, 10);

    if (index >= 0 && index < this.unusualActivities.length) {
      this.select(index);
    }
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    activityLabel(type) {
      return INSIGHTS.unusualActivity.labels[kebabCase(type)] || {};
    },
    closeDialog() {
      this.$emit('close');
    },
    copyLink() {
      navigator.clipboard.writeText(window.location.href);
      this.linkCopied = true;

      this.$nextTick(() => {
        $(this.$refs.copyLinkBtn).tooltip('show');
      });

      setTimeout(() => {
        this.linkCopied = false;
      }, 2000);
    },
    formatDateWithZone(startsAt) {
      const start = parseInternalDatetime(startsAt).tz(this.timeZone);

      return formatDate(start);
    },
    formatDuration(startsAt, stopsAt) {
      const start = parseInternalDatetime(startsAt);
      const stop = parseInternalDatetime(stopsAt);
      const duration = moment.duration(stop.diff(start));
      const hours = duration.hours();
      const minutes = duration.minutes();

      return `${hours ? `${hours}hr ` : ''}${minutes}m`;
    },
    formatRange(startsAt, stopsAt) {
      const start = parseInternalDatetime(startsAt);
      const stop = parseInternalDatetime(stopsAt);
      const startTime = start.format('h:mm a');
      const stopTime = stop.format('h:mm a');
      const timeZone = start.format('Z');

      return `${startTime}—${stopTime} ${timeZone}`;
    },
    formatTimeRange(startsAt, stopsAt) {
      const start = parseInternalDatetime(startsAt);
      const stop = parseInternalDatetime(stopsAt);
      const startTime = start.format('h:mm a');
      const stopTime = stop.format('h:mm a');
      const duration = moment.duration(stop.diff(start));
      const durationFormatted = `${padStart(duration.hours(), 1, '0')}:${padStart(duration.minutes(), 2, '0')}`;

      return `${startTime}—${stopTime} (${durationFormatted})`;
    },
    isNull,
    orderByPercentage(obj) {
      return sortBy(toPairs(obj || {}), ([, value]) => -value).map(([key, value]) => [key, Math.round(value)]);
    },
    screenshotCaption(screenshot) {
      return `<div class="d-flex justify-content-center gap-5 flex-wrap">
        ${screenshot.caption}
      </div>`;
    },
    secondsToMinutes(seconds) {
      return secondsToHours(Number.parseInt(seconds, 10) * 60, { padHours: true });
    },
    select(index) {
      this.selected = index;
      this.$emit('select', index);
      window.analytics?.track('Unusual instance selected', {
        instanceType: this.unusualActivities[index].type,
        confidenceLevel: this.unusualActivities[index].confidence_level,
        statsigCustomIDs: ['organizationID', this.organizationId],
      });
    },
    showGallery() {
      if (this.hasScreenshots && this.galleryEnabled) {
        window.analytics?.track('Unusual screenshot opened');
        this.$refs.gallery.openItemByIndex(0);
      }
    },
    suspiciousAppStyle(app) {
      return (this.selectedInstance.suspicious_apps || []).includes(app)
        ? 'color: white; background-color: var(--z-red-500)'
        : '';
    },
  },
};
</script>
